<template>
    <div class="perm-page">
        <!-- Access Denied -->
        <div v-if="!canAccessPermissions" class="perm-denied">
            <div class="perm-denied-icon"><i class="fas fa-shield-alt"></i></div>
            <h3>Access Restricted</h3>
            <p>Only SuperAdmin and Admin users can assign roles and permissions.</p>
        </div>

        <!-- Editor -->
        <div v-else>
            <!-- Header Bar -->
            <div class="perm-header">
                <div class="perm-header-left">
                    <div class="perm-icon-wrap"><i class="fas fa-user-shield"></i></div>
                    <h2 class="perm-title">Permission Editor</h2>
                </div>
                <div class="perm-header-right">
                    <div class="perm-role-selector" v-if="allowedUserRoles && allowedUserRoles.length">
                        <label class="perm-role-label">Role</label>
                        <select v-model="selectedUserRoleId" class="perm-role-select">
                            <option v-for="role in allowedUserRoles" :key="role.id" :value="role.id">{{ role.name }}</option>
                        </select>
                    </div>
                    <label class="perm-select-all">
                        <input type="checkbox" v-model="selectAllPermissions" @change="toggleSelectAll" class="perm-checkbox" />
                        <span>All</span>
                    </label>
                    <button class="perm-save-btn" @click="updatePermissions" :disabled="saving">
                        <i class="fas fa-save" v-if="!saving"></i>
                        <div class="perm-spin" v-else></div>
                        {{ saving ? "Saving..." : "Save" }}
                    </button>
                </div>
            </div>

            <!-- Stats Bar -->
            <div class="perm-stats" v-if="permissionsGrouped.length">
                <div class="perm-stat">
                    <span class="perm-stat-num">{{ totalPermissions }}</span>
                    <span class="perm-stat-label">Total</span>
                </div>
                <div class="perm-stat perm-stat--active">
                    <span class="perm-stat-num">{{ selectedPermissionIds.length }}</span>
                    <span class="perm-stat-label">Granted</span>
                </div>
                <div class="perm-stat perm-stat--muted">
                    <span class="perm-stat-num">{{ totalPermissions - selectedPermissionIds.length }}</span>
                    <span class="perm-stat-label">Denied</span>
                </div>
                <div class="perm-stat">
                    <span class="perm-stat-num">{{ permissionsGrouped.length }}</span>
                    <span class="perm-stat-label">Groups</span>
                </div>
            </div>

            <!-- Permission Groups Grid -->
            <div class="perm-grid" v-if="permissionsGrouped.length">
                <div
                    class="perm-group-card"
                    v-for="(group, gIdx) in permissionsGrouped"
                    :key="group.id"
                    :style="{ animationDelay: `${gIdx * 0.03}s` }"
                >
                    <div class="perm-group-header">
                        <div class="perm-group-icon" :class="getGroupColor(gIdx)">
                            <i :class="getGroupIcon(group.title)"></i>
                        </div>
                        <div class="perm-group-info">
                            <h4 class="perm-group-title">{{ group.title }}</h4>
                            <span class="perm-group-count">{{ getGroupSelectedCount(group) }}/{{ group.permissions.length }}</span>
                        </div>
                        <button class="perm-group-toggle" @click="toggleGroup(group)" :title="isGroupAllSelected(group) ? 'Deselect all' : 'Select all'">
                            <i :class="isGroupAllSelected(group) ? 'fas fa-toggle-on' : 'fas fa-toggle-off'"></i>
                        </button>
                    </div>
                    <div class="perm-group-body">
                        <label
                            class="perm-item"
                            v-for="permission in group.permissions"
                            :key="permission.id"
                            :class="{ 'perm-item--active': selectedPermissionIds.includes(permission.id) }"
                        >
                            <div class="perm-item-left">
                                <span class="perm-item-badge" :class="getPermBadgeClass(permission.name)">
                                    {{ getPermAction(permission.name) }}
                                </span>
                                <span class="perm-item-name">{{ formatPermName(permission.name) }}</span>
                            </div>
                            <input
                                type="checkbox"
                                class="perm-checkbox"
                                :checked="selectedPermissionIds.includes(permission.id)"
                                @click="handlePermissionAdd(permission.id)"
                            />
                        </label>
                    </div>
                </div>
            </div>

            <!-- Bottom Save -->
            <div class="perm-bottom-bar" v-if="permissionsGrouped.length">
                <span class="perm-bottom-info">{{ selectedPermissionIds.length }} of {{ totalPermissions }} permissions granted</span>
                <button class="perm-save-btn" @click="updatePermissions" :disabled="saving">
                    <i class="fas fa-save" v-if="!saving"></i>
                    <div class="perm-spin" v-else></div>
                    {{ saving ? "Saving..." : "Save Permissions" }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { http } from "@/utils/http"
import { mapActions, mapState } from "vuex"

export default {
    data() {
        return {
            loading: false,
            saving: false,
            selectedUserRoleId: "",
            selectedPermissionIds: [],
            selectAllPermissions: false,
        }
    },
    async mounted() {
        await this.initRoles()
    },
    methods: {
        ...mapActions("permission", ["initRoles", "initPermissions"]),
        getGroupColor(idx) {
            const colors = ["perm-clr-blue", "perm-clr-purple", "perm-clr-teal", "perm-clr-amber", "perm-clr-rose", "perm-clr-indigo"]
            return colors[idx % colors.length]
        },
        getGroupIcon(title) {
            if (!title) return "fas fa-lock"
            const t = title.toLowerCase()
            if (t.includes("employee")) return "fas fa-users"
            if (t.includes("attendance") || t.includes("shift")) return "fas fa-calendar-check"
            if (t.includes("leave")) return "fas fa-calendar-minus"
            if (t.includes("overtime")) return "fas fa-clock"
            if (t.includes("payroll") || t.includes("salary") || t.includes("payhead")) return "fas fa-money-bill-wave"
            if (t.includes("asset")) return "fas fa-boxes"
            if (t.includes("project") || t.includes("task")) return "fas fa-project-diagram"
            if (t.includes("company") || t.includes("compani")) return "fas fa-building"
            if (t.includes("department")) return "fas fa-sitemap"
            if (t.includes("designation")) return "fas fa-id-badge"
            if (t.includes("report")) return "fas fa-chart-bar"
            if (t.includes("setting")) return "fas fa-cog"
            if (t.includes("notice")) return "fas fa-bullhorn"
            if (t.includes("notification")) return "fas fa-bell"
            if (t.includes("file") || t.includes("document")) return "fas fa-folder"
            if (t.includes("communication")) return "fas fa-comments"
            if (t.includes("location")) return "fas fa-map-marker-alt"
            if (t.includes("template")) return "fas fa-file-alt"
            if (t.includes("bank")) return "fas fa-university"
            if (t.includes("cctv")) return "fas fa-video"
            if (t.includes("holiday")) return "fas fa-umbrella-beach"
            return "fas fa-lock"
        },
        getPermAction(name) {
            if (!name) return "?"
            const n = name.toUpperCase()
            if (n.startsWith("CREATE")) return "C"
            if (n.startsWith("VIEW")) return "V"
            if (n.startsWith("EDIT")) return "E"
            if (n.startsWith("DELETE")) return "D"
            return name.charAt(0)
        },
        getPermBadgeClass(name) {
            if (!name) return ""
            const n = name.toUpperCase()
            if (n.startsWith("CREATE")) return "perm-badge-create"
            if (n.startsWith("VIEW")) return "perm-badge-view"
            if (n.startsWith("EDIT")) return "perm-badge-edit"
            if (n.startsWith("DELETE")) return "perm-badge-delete"
            return ""
        },
        formatPermName(name) {
            if (!name) return ""
            return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
        },
        getGroupSelectedCount(group) {
            return group.permissions.filter((p) => this.selectedPermissionIds.includes(p.id)).length
        },
        isGroupAllSelected(group) {
            return group.permissions.every((p) => this.selectedPermissionIds.includes(p.id))
        },
        toggleGroup(group) {
            const allSelected = this.isGroupAllSelected(group)
            const groupIds = group.permissions.map((p) => p.id)
            if (allSelected) {
                this.selectedPermissionIds = this.selectedPermissionIds.filter((id) => !groupIds.includes(id))
            } else {
                const newIds = groupIds.filter((id) => !this.selectedPermissionIds.includes(id))
                this.selectedPermissionIds = [...this.selectedPermissionIds, ...newIds]
            }
        },
        async updatePermissions() {
            if (this.saving) return
            try {
                this.saving = true
                await http.patch("/permissions", {
                    permissionIds: this.selectedPermissionIds,
                    role_id: this.selectedUserRoleId,
                })
                this.$toast.success("Permissions updated successfully!")
            } catch (err) {
                console.log("error in updating permissions", err)
                this.$toast.error("Error updating permissions")
            }
            this.saving = false
        },
        handlePermissionAdd(pid) {
            if (this.selectedPermissionIds.includes(pid)) {
                this.selectedPermissionIds = this.selectedPermissionIds.filter((item) => item != pid)
            } else {
                this.selectedPermissionIds.push(pid)
            }
        },
        toggleSelectAll(event) {
            this.selectAllPermissions = event.target.checked
            if (this.selectAllPermissions) {
                this.selectedPermissionIds = this.permissionsGrouped.flatMap((group) => group.permissions.map((p) => p.id))
            } else {
                this.selectedPermissionIds = []
            }
        },
    },
    computed: {
        ...mapState("permission", ["userRoles", "permissionsGrouped", "allowedPermissionIds"]),
        ...mapState("auth", ["user"]),
        totalPermissions() {
            return this.permissionsGrouped.reduce((sum, g) => sum + g.permissions.length, 0)
        },
        isSuperAdmin() {
            return this.user?.role?.name?.toLowerCase() === "superadmin"
        },
        isAdmin() {
            return this.user?.role?.name?.toLowerCase() === "admin"
        },
        canAccessPermissions() {
            return this.isSuperAdmin || this.isAdmin
        },
        allowedUserRoles() {
            const filtered = this.userRoles.filter((item) => item.name.toLowerCase() !== "employee")
            if (this.isSuperAdmin) return filtered
            if (this.isAdmin) return filtered.filter((item) => item.name.toLowerCase() !== "superadmin")
            return []
        },
    },
    watch: {
        selectedUserRoleId(e) {
            this.initPermissions(e)
        },
        allowedPermissionIds(e) {
            this.selectedPermissionIds = e
        },
        userRoles(e) {
            if (e && e.length) {
                this.selectedUserRoleId = this.allowedUserRoles && this.allowedUserRoles[0] ? this.allowedUserRoles[0].id : ""
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.perm-page {
    animation: permFadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
@keyframes permFadeIn {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
}

/* ========== DENIED ========== */
.perm-denied {
    text-align: center;
    padding: 80px 20px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 10px;
    box-shadow: $shadowSm;

    h3 { font-size: 16px; font-weight: 700; color: $gray700; margin: 0 0 6px; }
    p { font-size: 13px; color: $gray500; margin: 0; max-width: 360px; margin: 0 auto; }
}
.perm-denied-icon {
    width: 56px;
    height: 56px;
    border-radius: $radiusFull;
    background: #fef2f2;
    color: $danger;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
    font-size: 22px;
}

/* ========== HEADER ========== */
.perm-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    padding: 10px 16px;
    margin-bottom: 10px;
    box-shadow: $shadowSm;
    flex-wrap: wrap;
}
.perm-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
}
.perm-icon-wrap {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: linear-gradient(135deg, $primaryLight, darken($primaryLight, 4%));
    color: $primaryColor;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
}
.perm-title {
    font-size: 15px;
    font-weight: 700;
    color: $gray800;
    margin: 0;
}
.perm-header-right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}
.perm-role-selector {
    display: flex;
    align-items: center;
    gap: 6px;
}
.perm-role-label {
    font-size: 11px;
    font-weight: 600;
    color: $gray500;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}
.perm-role-select {
    padding: 6px 10px;
    border: 1px solid $gray200;
    border-radius: 6px;
    font-size: 12.5px;
    color: $gray700;
    background: $gray50;
    outline: none;
    font-family: inherit;
    cursor: pointer;
    min-width: 140px;
    transition: all $transitionFast;

    &:focus { border-color: $primaryColor; box-shadow: 0 0 0 2px rgba(0, 94, 137, 0.08); }
}
.perm-select-all {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    color: $gray600;
    font-weight: 600;
    cursor: pointer;
    padding: 5px 10px;
    border-radius: 6px;
    border: 1px solid $gray200;
    background: white;
    transition: all $transitionFast;

    &:hover { background: $gray50; }
}
.perm-checkbox {
    width: 15px;
    height: 15px;
    accent-color: $primaryColor;
    cursor: pointer;
}
.perm-save-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 7px 14px;
    border: none;
    border-radius: 6px;
    background: $primaryColor;
    color: white;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    transition: all $transitionFast;
    font-family: inherit;
    white-space: nowrap;

    &:hover { background: $primaryDark; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
}
.perm-spin {
    width: 12px;
    height: 12px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: permSpin 0.7s linear infinite;
}
@keyframes permSpin { to { transform: rotate(360deg); } }

/* ========== STATS BAR ========== */
.perm-stats {
    display: flex;
    gap: 8px;
    margin-bottom: 10px;
}
.perm-stat {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding: 10px 8px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    box-shadow: $shadowSm;
}
.perm-stat-num {
    font-size: 18px;
    font-weight: 800;
    color: $gray700;
    line-height: 1;
}
.perm-stat-label {
    font-size: 10px;
    font-weight: 600;
    color: $gray400;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.perm-stat--active .perm-stat-num { color: #059669; }
.perm-stat--muted .perm-stat-num { color: $gray400; }

/* ========== GRID ========== */
.perm-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    margin-bottom: 12px;
}
.perm-group-card {
    background: white;
    border: 1px solid $gray100;
    border-radius: 10px;
    box-shadow: $shadowSm;
    overflow: hidden;
    animation: permSlideIn 0.25s cubic-bezier(0.4, 0, 0.2, 1) both;
}
@keyframes permSlideIn {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
}
.perm-group-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border-bottom: 1px solid $gray100;
    background: $gray50;
}
.perm-group-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    flex-shrink: 0;
}
.perm-clr-blue { background: #eff6ff; color: #2563eb; }
.perm-clr-purple { background: #f5f3ff; color: #7c3aed; }
.perm-clr-teal { background: #f0fdfa; color: #0d9488; }
.perm-clr-amber { background: #fffbeb; color: #d97706; }
.perm-clr-rose { background: #fff1f2; color: #e11d48; }
.perm-clr-indigo { background: #eef2ff; color: #4f46e5; }

.perm-group-info {
    flex: 1;
    min-width: 0;
}
.perm-group-title {
    font-size: 13px;
    font-weight: 700;
    color: $gray800;
    margin: 0;
    line-height: 1.2;
}
.perm-group-count {
    font-size: 10.5px;
    font-weight: 600;
    color: $gray400;
}
.perm-group-toggle {
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
    color: $gray400;
    padding: 0;
    line-height: 1;
    transition: color $transitionFast;

    &:hover { color: $primaryColor; }

    .fas.fa-toggle-on { color: $primaryColor; }
}

/* ========== PERMISSION ITEMS ========== */
.perm-group-body {
    display: flex;
    flex-direction: column;
}
.perm-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 7px 14px;
    cursor: pointer;
    transition: background $transitionFast;
    border-bottom: 1px solid $gray50;
    margin: 0;

    &:last-child { border-bottom: none; }
    &:hover { background: $gray50; }
    &--active { background: rgba(0, 94, 137, 0.015); }
}
.perm-item-left {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}
.perm-item-badge {
    width: 22px;
    height: 22px;
    border-radius: 5px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 800;
    flex-shrink: 0;
}
.perm-badge-create { background: #ecfdf5; color: #059669; }
.perm-badge-view { background: #eff6ff; color: #2563eb; }
.perm-badge-edit { background: #fffbeb; color: #d97706; }
.perm-badge-delete { background: #fef2f2; color: #dc2626; }

.perm-item-name {
    font-size: 12px;
    color: $gray600;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    .perm-item--active & {
        color: $gray800;
        font-weight: 600;
    }
}

/* ========== BOTTOM BAR ========== */
.perm-bottom-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    padding: 10px 16px;
    box-shadow: $shadowSm;
}
.perm-bottom-info {
    font-size: 12px;
    color: $gray500;
    font-weight: 500;
}

/* ========== RESPONSIVE ========== */
@media (max-width: 768px) {
    .perm-header { flex-direction: column; align-items: stretch; padding: 10px 12px; }
    .perm-header-right { flex-wrap: wrap; }
    .perm-role-select { min-width: 100px; }
    .perm-stats { flex-wrap: wrap; }
    .perm-stat { min-width: calc(50% - 6px); }
    .perm-grid { grid-template-columns: 1fr; }
    .perm-bottom-bar { flex-direction: column; gap: 8px; text-align: center; }
}
@media (max-width: 480px) {
    .perm-header { padding: 8px 10px; }
    .perm-item { padding: 6px 10px; }
    .perm-group-header { padding: 10px; }
}
</style>
